import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { SessionAuth } from "supertokens-auth-react/recipe/session";
import CustomSessionAuth from "./views/components/CustomSessionAuth";
import { EmailPasswordPreBuiltUI } from "supertokens-auth-react/recipe/emailpassword/prebuiltui";
import { EmailVerificationPreBuiltUI } from "supertokens-auth-react/recipe/emailverification/prebuiltui";
import { getSuperTokensRoutesForReactRouterDom } from "supertokens-auth-react/ui";
import Layout from "./views/pages/Layout";
import AuthPage from "./views/pages/auth/AuthPage";
import AccessDenied from "./views/pages/AccessDenied";
import RoleAuth from "./views/components/RoleAuth";
import { Box, CircularProgress } from "@mui/material";
import { CustomThemeProvider } from "./context/ThemeContext";

import { useGlobalError } from "./context/GlobalErrorContext";
import setupAxiosInterceptors from "./utils/axiosConfig";


// Lazy load page components
const Dashboard = React.lazy(() => import("./views/pages/Dashboard"));
const CourseRouteHandler = React.lazy(() => import("./views/pages/CourseRouteHandler"));
const StudentCoursePage = React.lazy(() => import("./views/pages/StudentCoursePage"));
const AssignmentPage = React.lazy(() => import("./views/pages/AssignmentPage"));
const SubmissionView = React.lazy(() => import("./views/pages/SubmissionView"));
const LtiSelectPage = React.lazy(() => import("./views/pages/LtiSelectPage"));
const AdminDashboard = React.lazy(() => import("./views/pages/AdminDashboard"));
const AccountPage = React.lazy(() => import("./views/pages/AccountPage"));
const SignUpPage = React.lazy(() => import("./views/pages/auth/SignUpPage"));
const TermsOfServicePage = React.lazy(() => import("./views/pages/TermsOfServicePage"));
const PrivacyPolicyPage = React.lazy(() => import("./views/pages/PrivacyPolicyPage"));
const ShareLinkHandler = React.lazy(() => import("./views/pages/ShareLinkHandler"));

// Inner App component to use hooks from providers
const AppContent = () => {
    const { showError } = useGlobalError();

    useEffect(() => {
        setupAxiosInterceptors(showError);
    }, [showError]);

    return (
        <Router>
            <Suspense fallback={
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                    <CircularProgress />
                </Box>
            }>
                <Routes>
                    {/* SuperTokens default routes (handles /auth/verify-email because of our overrides) */}
                    {getSuperTokensRoutesForReactRouterDom(require("react-router-dom"), [EmailPasswordPreBuiltUI, EmailVerificationPreBuiltUI])}

                    {/* A single, unified authentication route for Login/Signup */}
                    <Route path="/auth/*" element={<AuthPage />} />
                    <Route path="/signup" element={<SignUpPage />} />

                    {/* Legal Routes */}
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                    {/* LTI Selection Route - needs auth but maybe different layout or just SessionAuth */}
                    {/* LTI Page does NOT use the main Layout because it's usually embedded */}
                    <Route
                        path="/lti/select"
                        element={<SessionAuth><LtiSelectPage /></SessionAuth>}
                    />

                    {/* Share Link Handler - redirects, doesn't need Layout */}
                    <Route
                        path="/share/assignment/:shareToken"
                        element={<CustomSessionAuth><ShareLinkHandler /></CustomSessionAuth>}
                    />


                    {/* Main Application Routes wrapped in Layout */}
                    <Route element={
                        <CustomSessionAuth>
                            <Layout />
                        </CustomSessionAuth>
                    }>
                        <Route
                            path="/"
                            element={<Dashboard />}
                        />
                        <Route
                            path="/admin"
                            element={
                                <RoleAuth allowedRoles={['admin']} doRedirect={true}>
                                    <AdminDashboard />
                                </RoleAuth>
                            }
                        />
                        <Route
                            path="/account"
                            element={
                                <RoleAuth allowedRoles={['admin', 'instructor']} doRedirect={true}>
                                    <AccountPage />
                                </RoleAuth>
                            }
                        />
                        <Route
                            path="/course/:courseId"
                            element={<CourseRouteHandler />}
                        />
                        <Route
                            path="/course/:courseId/student/:studentId"
                            element={<StudentCoursePage />}
                        />
                        <Route
                            path="/course/:courseId/assignment/:assignmentId"
                            element={<AssignmentPage />}
                        />
                        <Route
                            path="/course/:courseId/assignment/:assignmentId/submission/:submissionId"
                            element={<SubmissionView />}
                        />
                        <Route path="/access-denied" element={<AccessDenied />} />
                    </Route>
                </Routes>
            </Suspense>
        </Router>
    );
};

function App() {
    return (
        <CustomThemeProvider>
            <AppContent />
        </CustomThemeProvider>
    );
}

export default App;